import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch, batch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Typography,
  MenuItem
} from '@mui/material';
import useRouteAction from 'state/hooks/route-action/use-route-action';
import { getUserAssignmentsView } from 'state/modules/entity/thunks/assignment';
import { getUserLessonPlans } from 'state/modules/entity/thunks/teaching-order';
import {
  fetchLessonPlan,
  fetchLessonPlanWithAssignments
} from 'state/modules/entity/thunks/lesson-planner';
import { selectClassroom } from 'state/modules/app/actions';
import { translate } from 'translations/translator';
import { LESSON_PLANNER_MODE, AppStoreState } from 'types';
import { buildSearchQueryParams } from 'util/query-params-util';
import LessonPlannerTranslationVariant from 'translations/variants/lessonPlanner';
import StyledBaseInput from 'components/@common/Inputs/StyledBaseInput';
import { setLessonPlannerNavbarAttribute } from '../../../state/modules/lessonPlanner/thunks';

const getTranslation = (key: LessonPlannerTranslationVariant | string) =>
  translate('lessonPlanner', key);

const LessonPlannerHeader: React.FC<any> = ({ highlight = false }) => {
  const classes = useStyles()();
  const dispatch = useDispatch() as any;
  const navigate = useNavigate();
  const location = useLocation();

  const { bookCode, edition } = useParams();
  const { mode }: any = useSelector((state: AppStoreState) => state.lessonPlanner, shallowEqual);

  const classroomId = useSelector(({ app }: AppStoreState) => app.selectedClassroomId);
  const teachingOrders: any = useSelector(({ entity }: AppStoreState) => entity.teachingOrder);
  const classrooms: any = useSelector(({ entity }: AppStoreState) => entity.classrooms);
  const user = useSelector(({ user }: AppStoreState) => user);

  useRouteAction(({ action }) => {
    switch (action) {
      case 'move-lesson':
        return;
    }
  });

  const handleChange = event => {
    switch (event.target.name) {
      case 'teachingOrder':
        handleSelectTeachingOrder(event);
        break;
      case 'classroom':
        handleSelectClassroom(event);
        break;
      default:
        dispatch(fetchLessonPlan());
        return;
    }

    dispatch(setLessonPlannerNavbarAttribute(event.target.name, event.target.value));
  };

  const handleManageTeachingOrder = () => navigate(`${location.pathname}/manage-options`);

  const handleSelectClassroom = ({ target: { value } }) => {
    const classroomId = Number(value);

    batch(() => {
      dispatch(selectClassroom(classroomId));
      dispatch(setLessonPlannerNavbarAttribute('classroomId', classroomId));
      dispatch(fetchLessonPlanWithAssignments(classroomId));
      dispatch(getUserAssignmentsView(classroomId));
    });
  };

  const handleSelectTeachingOrder = ({ target: { value } }) => {
    const currentTeachingOrder = teachingOrders.find(x => x.id && x.name === value.name);

    if (currentTeachingOrder?.id) dispatch(getUserLessonPlans(currentTeachingOrder));

    const active = currentTeachingOrder.active;
    navigate({
      pathname: `/books/${bookCode}/${edition}/lesson-planner`,
      search: buildSearchQueryParams({ active })
    });
  };

  return (
    <Container
      id={'filterBarGroup'}
      maxWidth="xl"
      style={{
        backgroundColor: '#fff',
        marginTop: ' 13px',
        position: 'relative',
        zIndex: highlight ? 1000 : 'inherit'
      }}
    >
      <Grid
        container
        spacing={3}
        alignItems="flex-end"
        sx={{
          width: 'calc(100% + 24px)',
          margin: '-12px'
        }}
      >
        <div className={classes.dropdownGroup}>
          <Grid item>
            {!user.isAuditor && (
              <FormControl
                className={classes.dropdown}
                sx={{ marginBottom: '36px', marginLeft: '10px' }}
                variant="standard"
              >
                <InputLabel
                  id="classroomLabel"
                  sx={{
                    fontWeight: 400,
                    fontSize: '16px',
                    color: 'black !important',
                    marginLeft: '5px'
                  }}
                >
                  {getTranslation(LessonPlannerTranslationVariant.selectClassroom)}
                </InputLabel>
                <Select
                  labelId="classroomLabel"
                  className={classes.dropdown}
                  value={classroomId}
                  name="classroom"
                  onChange={handleChange}
                  input={<StyledBaseInput />}
                  sx={{ marginTop: '20px !important' }}
                >
                  {classrooms?.map((x, idx) => (
                    <MenuItem key={`${idx}-classroom`} value={x.id}>
                      {x.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          {mode === LESSON_PLANNER_MODE.LESSON_PLANNER && (
            <Button
              disabled={!classrooms.length}
              className={classes.manageButton}
              onClick={handleManageTeachingOrder}
            >
              <FontAwesomeIcon icon={faWrench} className={classes.wrenchIcon} size="sm" />
              <Typography>{getTranslation('manageOptions')}</Typography>
            </Button>
          )}
        </div>
      </Grid>
    </Container>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    dropdown: {
      width: '350px',
      height: '37px',
      borderRadius: '4px',

      backgroundColor: '#FFFFFF',
      '&.Mui-focused': {
        backgroundColor: '#FFFFFF'
      },
      '&:active': {
        backgroundColor: '#FFFFFF'
      },
      '&.MuiSelect-select': {
        '&:focus': {
          backgroundColor: '#FFFFFF'
        }
      }
    },
    dropdownGroup: {
      display: 'flex',
      flex: 1,
      justifyContent: 'space-between',
      margin: 18,
      flexWrap: 'wrap',
      '& .MuiButton-root': {
        '&:hover': {
          textDecoration: 'none',
          backgroundColor: 'white'
        }
      },
      '& .MuiButton-text': {
        textTransform: 'none',
        alignSelf: 'center',
        whiteSpace: 'nowrap',
        color: '#2878C1',
        '&:hover': {
          color: '#194C7B',
          textDecoration: 'none'
        }
      }
    },
    smallTextButton: {
      fontSize: 16,
      width: '30px',
      color: '#000000',
      '&.Mui-focused': {
        backgroundColor: '#FFFFFF'
      },
      '&:active': {
        backgroundColor: '#FFFFFF'
      },
      '&:hover': {
        backgroundColor: '#FFFFFF'
      },
      '&:disabled': {
        border: 'none'
      },
      margin: '0 10px',
      textTransform: 'none'
    },
    manageButton: {
      textTransform: 'none',
      alignSelf: 'center',
      whiteSpace: 'nowrap'
    },
    toggleGroup: {
      display: 'flex',
      flex: 1,
      justifyContent: 'flex-end',
      margin: '6px 48px',
      alignSelf: 'center'
    },
    wrenchIcon: {
      margin: '0 10px'
    },
    iconClass: {
      margin: '0 10px'
    },
    teachingOrder: {
      display: 'flex',
      height: 429 - 66,
      width: 616,
      paddingTop: 0
    }
  }));

export default LessonPlannerHeader;
